import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { WifiOff, Wifi, RefreshCw, Loader2, AlertCircle, CheckCircle2, Cloud } from 'lucide-react';
import { offlineDB } from '@/lib/offline/offlineDB';
import { syncService } from '@/lib/offline/syncService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useOnlineStatus } from '@/hooks/offline/useOnlineStatus';

interface PendingLog {
  visitId: string;
  userId: string;
  contactPerson: string;
  contactRole: string;
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  error?: string;
  retries: number;
}

export const OfflineSyncBadge = () => {
  // Estado de conexión centralizado
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLogs, setPendingLogs] = useState<PendingLog[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Cargar logs pendientes
  const loadPendingLogs = async () => {
    const logs = await offlineDB.getPendingLogs();

    // Ordenar por timestamp ascendente para una lectura más predecible
    const sorted = [...logs].sort((a, b) => a.timestamp - b.timestamp);

    setPendingLogs(sorted);
    setPendingCount(sorted.length);
  };

  // Escuchar cambios en IndexedDB
  useEffect(() => {
    loadPendingLogs();

    const handleUpdate = () => {
      loadPendingLogs();
    };

    window.addEventListener('offline-logs-updated', handleUpdate);
    return () => window.removeEventListener('offline-logs-updated', handleUpdate);
  }, []);

  // Sincronizar todos
  const handleSyncAll = async () => {
    setIsSyncing(true);
    await syncService.syncAll();
    await loadPendingLogs();
    setIsSyncing(false);
  };

  // si no hay pendientes y esta online, no mostrar nada
  if (pendingCount === 0 && isOnline) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          variant={isOnline ? "outline" : "destructive"}
          size="sm"
          className="relative shadow-lg cursor-pointer"
        >
          {isOnline ? (
            <Wifi className="h-4 w-4 mr-2" />
          ) : (
            <WifiOff className="h-4 w-4 mr-2" />
          )}

          <span className="hidden sm:inline">
            {isOnline ? 'Conectado' : 'Sin conexión'}
          </span>

          {pendingCount > 0 && (
            <Badge
              variant="destructive"
              className="ml-2 px-1.5 py-0.5 text-xs font-bold"
            >
              {pendingCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Estado de Sincronización
          </DialogTitle>
          <DialogDescription>
            {isOnline
              ? `${pendingCount} bitácora(s) pendiente(s) de sincronizar`
              : 'Sin conexión a internet - Modo offline activo'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* estado de red */}
          <Alert variant={isOnline ? "default" : "destructive"}>
            {isOnline ? (
              <Wifi className="h-5 w-5 text-green-500 dark:text-green-600" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-500 text:bg-red-800" />
            )}

            <AlertDescription className="w-full">
              {isOnline
                ? 'Conexión a internet disponible'
                : 'Sin conexión - Las bitácoras se guardarán localmente'}
            </AlertDescription>
          </Alert>

          {/* boton de sincronizacion */}
          {pendingCount > 0 && isOnline && (
            <Button
              onClick={handleSyncAll}
              disabled={isSyncing}
              className="w-full cursor-pointer"
              size="lg"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sincronizar {pendingCount} bitácora(s)
                </>
              )}
            </Button>
          )}

          {/* Bitacoras pendientes */}
          {pendingLogs.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Bitácoras pendientes:
              </h3>

              {pendingLogs.map((log) => (
                <Card key={log.visitId} className="border-l-4 border-l-orange-500">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          {log.contactPerson}
                          {log.status === 'pending' && (
                            <Badge variant="outline" className="text-xs">
                              Pendiente
                            </Badge>
                          )}
                          {log.status === 'syncing' && (
                            <Badge variant="default" className="text-xs">
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Sincronizando
                            </Badge>
                          )}
                          {log.status === 'failed' && (
                            <Badge variant="destructive" className="text-xs">
                              Error
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {log.contactRole || 'Sin cargo especificado'}
                        </CardDescription>
                      </div>

                      {log.status === 'pending' && (
                        <CheckCircle2 className="h-5 w-5 text-orange-500" />
                      )}
                      {log.status === 'failed' && (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pb-3">
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>
                        Guardado: {new Date(log.timestamp).toLocaleString('es-ES')}
                      </p>
                      {log.retries > 0 && (
                        <p className="text-orange-600">
                          Intentos: {log.retries}
                        </p>
                      )}
                      {log.error && (
                        <p className="text-red-600">
                          Error: {log.error}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No hay bitácoras pendientes de sincronizar
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
